import { useId, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { createPrepTemplate, deletePrepTemplate, PREP_MAX_DAYS_BEFORE, PREP_MAX_STEPS, PREP_MIN_DAYS_BEFORE, updatePrepTemplate, type TemplateIssue, type Weekday } from '@autoday/core';
import { getStore, useStore } from '../../store/appStore.ts';
import { Button, Field, IconButton, Sheet, inputCls } from '../../ui/kit.tsx';
import { errorText, toast } from '../../ui/toast.tsx';
import { randomId } from '../../platform.ts';
import { useLang } from '../../i18n/i18n.ts';
import { calendarName, formatClock, formatMinutes, weekdayName } from '../../i18n/format.ts';
import {
  draftContent,
  draftIssue,
  durationOptions,
  emptyTemplateDraft,
  moveItem,
  nextStepDraft,
  ownCalendars,
  quarterTimes,
  stepDaysText,
  templateDraftOf,
  templateIssueText,
  WINDOW_PRESETS,
  weekdaysText,
  windowPresetId,
  type TemplateDraft,
} from './prepText.ts';

const ALL_DAYS: Weekday[] = [1, 2, 3, 4, 5, 6, 7];
const WORK_DAYS: Weekday[] = [1, 2, 3, 4, 5];
const DAYS_BEFORE = Array.from({ length: PREP_MAX_DAYS_BEFORE - PREP_MIN_DAYS_BEFORE + 1 }, (_, i) => PREP_MIN_DAYS_BEFORE + i);
const hhmm = (s: string) => formatClock(Number(s.slice(0, 2)), Number(s.slice(3, 5)));
/** Fel som gäller valen under "Tider och kalender". */
const WHEN_CODES = new Set<TemplateIssue['code']>(['day-count', 'weekdays', 'window', 'window-short', 'calendar']);

/**
 * Skapa eller ändra en förberedelsemall: namn och 1–5 ordnade steg på första nivån; dagräkning, tid på dagen,
 * veckodagar och kalender under "Tider och kalender". Samma kontroll som core (validatePrepTemplateContent).
 * Befintliga steg behåller sina ID:n; en ändring påverkar aldrig planer som redan lagts in. Mallnamn och
 * stegtexter är användarens egna och översätts aldrig.
 */
export function TemplateEditor({ templateId, onClose, onSaved }: { templateId: string | null; onClose: () => void; onSaved?: (id: string) => void }) {
  const { state } = useStore();
  const { t } = useLang();
  const ids = useId();
  const existing = templateId ? state.prepTemplates?.[templateId] : undefined;
  const [base] = useState(() => (existing ? { revision: existing.revision } : null));
  const [draft, setDraft] = useState<TemplateDraft>(() => (existing ? templateDraftOf(existing) : emptyTemplateDraft(state)));
  const [seq, setSeq] = useState(0);
  const [customWindow, setCustomWindow] = useState(() => windowPresetId(draft.window) === 'custom');
  const [moreOpen, setMoreOpen] = useState(!existing);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [announce, setAnnounce] = useState('');
  // Ett operations-ID per öppnat formulär: ett dubbelklick på Spara skapar aldrig två mallar.
  const [opId] = useState(() => randomId('tpl_'));
  const gone = !!templateId && !existing;
  const issue = draftIssue(state, draft);
  const shown = tried ? issue : null;
  const calendars = ownCalendars(state);
  const store = getStore();

  const set = (patch: Partial<TemplateDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const setStep = (i: number, patch: Partial<TemplateDraft['steps'][number]>) => setDraft((d) => ({ ...d, steps: d.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const fieldId = (what: string) => `${ids}-${what}`;
  const stepError = (i: number) => (shown && shown.stepIndex === i && shown.code.startsWith('step') ? templateIssueText(shown) : null);

  function focusIssue(x: TemplateIssue) {
    if (WHEN_CODES.has(x.code)) setMoreOpen(true);
    const target =
      x.code === 'name'
        ? fieldId('name')
        : x.code === 'step-title'
          ? fieldId(`title-${x.stepIndex ?? 0}`)
          : x.code === 'step-duration'
            ? fieldId(`dur-${x.stepIndex ?? 0}`)
            : x.code === 'step-days' || x.code === 'step-order'
              ? fieldId(`days-${x.stepIndex ?? 0}`)
              : x.code === 'calendar'
                ? fieldId('calendar')
                : x.code === 'window' || x.code === 'window-short'
                  ? fieldId('window')
                  : fieldId('more');
    requestAnimationFrame(() => document.getElementById(target)?.focus());
  }

  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= draft.steps.length) return;
    setDraft((d) => ({ ...d, steps: moveItem(d.steps, i, dir) }));
    setAnnounce(dir < 0 ? t(`Steg ${i + 1} är nu steg ${j + 1}.`, `Step ${i + 1} is now step ${j + 1}.`) : t(`Steg ${i + 1} är nu steg ${j + 1}.`, `Step ${i + 1} is now step ${j + 1}.`));
  }

  async function save() {
    if (busy || gone) return;
    if (issue) {
      setTried(true);
      focusIssue(issue);
      return;
    }
    setBusy(true);
    let id = templateId ?? '';
    try {
      await store.run((s) => {
        if (templateId) return updatePrepTemplate(s, opId, templateId, draftContent(draft), store.clock.now(), base?.revision);
        const r = createPrepTemplate(s, opId, draftContent(draft), store.clock.now());
        id = r.id;
        return r;
      });
      toast({ kind: 'ok', text: templateId ? t('Mallen är sparad. Planer som redan lagts in ändras inte.', 'The template is saved. Plans already added do not change.') : t('Mallen är skapad.', 'The template is created.') });
      onSaved?.(id);
      onClose();
    } catch (e) {
      toast({ kind: 'error', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!templateId || busy) return;
    setBusy(true);
    let paused = 0;
    try {
      await store.run((s) => {
        const r = deletePrepTemplate(s, opId + ':del', templateId, store.clock.now());
        paused = r.pausedRules.length;
        return r;
      });
      toast({
        kind: 'ok',
        text:
          t('Mallen är borttagen. Planer som redan lagts in påverkas inte.', 'The template is deleted. Plans already added are not affected.') +
          (paused ? ' ' + (paused === 1 ? t('1 regel pausades.', '1 rule was paused.') : t(`${paused} regler pausades.`, `${paused} rules were paused.`)) : ''),
      });
      onClose();
    } catch (e) {
      toast({ kind: 'error', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  const presetId = customWindow ? 'custom' : windowPresetId(draft.window);
  const whenSummary = [
    draft.dayCount === 'weekdays' ? t('Räknar vardagar', 'Counts weekdays') : t('Räknar kalenderdagar', 'Counts calendar days'),
    `${hhmm(draft.window.start)}–${hhmm(draft.window.end)}`,
    weekdaysText(draft.weekdays),
    calendarName(state.calendars[draft.calendarId], t('ingen kalender vald', 'no calendar chosen')),
  ].join(' · ');

  return (
    <Sheet
      open
      onOpenChange={(o) => !o && onClose()}
      title={templateId ? t('Ändra mall', 'Edit template') : t('Ny mall', 'New template')}
      description={t('Beskriv hur du brukar förbereda dig. Stegen läggs i tidsordning före deadline.', 'Describe how you usually prepare. The steps are placed in time order before the deadline.')}
      footer={
        <>
          {templateId && !gone && (
            <Button variant="danger" className="mr-auto" icon={<Trash2 size={16} aria-hidden />} onClick={() => setConfirmDelete(true)} disabled={busy}>
              {t('Ta bort mallen', 'Delete the template')}
            </Button>
          )}
          <Button onClick={onClose}>{t('Avbryt', 'Cancel')}</Button>
          <Button variant="primary" onClick={() => void save()} disabled={busy || gone} aria-describedby={shown ? fieldId('issue') : undefined}>
            {t('Spara mallen', 'Save the template')}
          </Button>
        </>
      }
    >
      {gone ? (
        <p className="text-sm">{t('Mallen finns inte längre. Den kan ha tagits bort på en annan enhet eller i en annan flik.', 'The template no longer exists. It may have been deleted on another device or in another tab.')}</p>
      ) : (
        <div className="grid gap-5 text-sm">
          <p className="sr-only" aria-live="polite">
            {announce}
          </p>
          <Field label={t('Namn på mallen', 'Template name')} htmlFor={fieldId('name')} error={shown?.code === 'name' ? templateIssueText(shown) : null}>
            <input id={fieldId('name')} className={inputCls} value={draft.name} maxLength={80} onChange={(e) => set({ name: e.target.value })} placeholder={t('t.ex. Förbered i två steg', 'e.g. Prepare in two steps')} />
          </Field>

          <fieldset className="grid gap-3">
            <legend className="mb-1 text-sm font-medium">{t('Steg i tidsordning', 'Steps in time order')}</legend>
            <p className="-mt-1 text-xs text-muted">{t('Det första steget ligger flest dagar före deadline. Varje steg blir ett eget pass i kalendern.', 'The first step is the most days before the deadline. Each step becomes its own session in the calendar.')}</p>
            <ol className="grid gap-3">
              {draft.steps.map((s, i) => {
                const err = stepError(i);
                const n = i + 1;
                return (
                  <li key={s.key} className={`grid min-w-0 gap-3 rounded-lg border p-3 ${err ? 'border-risk/70' : 'border-border'}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold">{t(`Steg ${n}`, `Step ${n}`)}</span>
                      <span className="flex gap-1">
                        <IconButton label={t(`Flytta steg ${n} upp`, `Move step ${n} up`)} aria-disabled={i === 0} className={i === 0 ? 'opacity-40' : ''} onClick={() => move(i, -1)}>
                          <ArrowUp size={18} aria-hidden />
                        </IconButton>
                        <IconButton label={t(`Flytta steg ${n} ned`, `Move step ${n} down`)} aria-disabled={i === draft.steps.length - 1} className={i === draft.steps.length - 1 ? 'opacity-40' : ''} onClick={() => move(i, 1)}>
                          <ArrowDown size={18} aria-hidden />
                        </IconButton>
                        <IconButton
                          label={t(`Ta bort steg ${n}`, `Remove step ${n}`)}
                          aria-disabled={draft.steps.length <= 1}
                          className={draft.steps.length <= 1 ? 'opacity-40' : 'hover:text-risk'}
                          onClick={() => {
                            if (draft.steps.length <= 1) return;
                            setDraft((d) => ({ ...d, steps: d.steps.filter((_, j) => j !== i) }));
                            setAnnounce(t(`Steg ${n} är borttaget.`, `Step ${n} is removed.`));
                          }}
                        >
                          <Trash2 size={18} aria-hidden />
                        </IconButton>
                      </span>
                    </div>
                    <Field label={t('Vad gör du?', 'What do you do?')} htmlFor={fieldId(`title-${i}`)}>
                      <input id={fieldId(`title-${i}`)} className={inputCls} value={s.title} maxLength={120} onChange={(e) => setStep(i, { title: e.target.value })} placeholder={i === 0 ? t('t.ex. Samla underlag', 'e.g. Gather material') : ''} />
                    </Field>
                    <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(9rem,1fr))]">
                      <Field label={t('Längd', 'Length')} htmlFor={fieldId(`dur-${i}`)}>
                        <select id={fieldId(`dur-${i}`)} className={inputCls} value={s.durationMinutes} onChange={(e) => setStep(i, { durationMinutes: Number(e.target.value) })}>
                          {durationOptions(s.durationMinutes).map((m) => (
                            <option key={m} value={m}>
                              {formatMinutes(m)}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label={t('Helst', 'Preferably')} htmlFor={fieldId(`days-${i}`)}>
                        <select id={fieldId(`days-${i}`)} className={inputCls} value={s.daysBefore} onChange={(e) => setStep(i, { daysBefore: Number(e.target.value) })}>
                          {DAYS_BEFORE.map((d) => (
                            <option key={d} value={d}>
                              {stepDaysText(d, draft.dayCount)}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    {err && (
                      <p className="text-xs font-medium text-risk" role="alert">
                        {err}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
            <div>
              <Button
                icon={<Plus size={16} aria-hidden />}
                disabled={draft.steps.length >= PREP_MAX_STEPS}
                onClick={() => {
                  const key = `n${seq + 1}`;
                  setSeq((x) => x + 1);
                  setDraft((d) => ({ ...d, steps: [...d.steps, nextStepDraft(d.steps, key)] }));
                  setAnnounce(t(`Steg ${draft.steps.length + 1} är tillagt.`, `Step ${draft.steps.length + 1} is added.`));
                  requestAnimationFrame(() => document.getElementById(fieldId(`title-${draft.steps.length}`))?.focus());
                }}
              >
                {t('Lägg till steg', 'Add step')}
              </Button>
              {draft.steps.length >= PREP_MAX_STEPS && <p className="mt-1 text-xs text-muted">{t(`En mall har högst ${PREP_MAX_STEPS} steg.`, `A template has at most ${PREP_MAX_STEPS} steps.`)}</p>}
            </div>
          </fieldset>

          <details className="rounded-lg border border-border" open={moreOpen} onToggle={(e) => setMoreOpen((e.currentTarget as HTMLDetailsElement).open)}>
            <summary id={fieldId('more')} className="min-h-11 cursor-pointer px-3 py-3">
              <span className="font-medium">{t('Tider och kalender', 'Times and calendar')}</span>
              <span className="block text-xs text-muted">{whenSummary}</span>
            </summary>
            <div className="grid gap-4 border-t border-border p-3">
              <fieldset>
                <legend className="mb-1.5 text-sm font-medium">{t('Räkna dagar som', 'Count days as')}</legend>
                <div className="flex flex-wrap gap-x-4">
                  <label className="flex min-h-11 items-center gap-2">
                    <input type="radio" className="h-5 w-5" name={fieldId('daycount')} checked={draft.dayCount === 'weekdays'} onChange={() => set({ dayCount: 'weekdays' })} />
                    {t('Vardagar (mån–fre)', 'Weekdays (Mon–Fri)')}
                  </label>
                  <label className="flex min-h-11 items-center gap-2">
                    <input type="radio" className="h-5 w-5" name={fieldId('daycount')} checked={draft.dayCount === 'calendar'} onChange={() => set({ dayCount: 'calendar' })} />
                    {t('Kalenderdagar', 'Calendar days')}
                  </label>
                </div>
                <p className="mt-1 text-xs text-muted">{t('Helgdagar räknas som vanliga dagar – AutoDay känner inte till röda dagar.', 'Public holidays count as ordinary days – AutoDay does not know about them.')}</p>
              </fieldset>

              <div className="grid gap-3">
                <Field label={t('Tid på dagen', 'Time of day')} htmlFor={fieldId('window')} error={shown && (shown.code === 'window' || shown.code === 'window-short') ? templateIssueText(shown) : null}>
                  <select
                    id={fieldId('window')}
                    className={inputCls}
                    value={presetId}
                    onChange={(e) => {
                      const p = WINDOW_PRESETS.find((x) => x.id === e.target.value);
                      setCustomWindow(!p);
                      if (p) set({ window: { start: p.start, end: p.end } });
                    }}
                  >
                    {WINDOW_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {t(p.sv, p.en)}
                      </option>
                    ))}
                    <option value="custom">{t('Egen tid', 'Custom time')}</option>
                  </select>
                </Field>
                {presetId === 'custom' && (
                  <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(8rem,1fr))]">
                    <Field label={t('Från', 'From')} htmlFor={fieldId('from')}>
                      <select id={fieldId('from')} className={inputCls} value={draft.window.start} onChange={(e) => set({ window: { ...draft.window, start: e.target.value } })}>
                        {quarterTimes().map((q) => (
                          <option key={q} value={q}>
                            {hhmm(q)}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label={t('Till', 'To')} htmlFor={fieldId('to')}>
                      <select id={fieldId('to')} className={inputCls} value={draft.window.end} onChange={(e) => set({ window: { ...draft.window, end: e.target.value } })}>
                        {quarterTimes().map((q) => (
                          <option key={q} value={q}>
                            {hhmm(q)}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                )}
              </div>

              <fieldset>
                <legend className="mb-1.5 text-sm font-medium">{t('Tillåtna veckodagar', 'Allowed weekdays')}</legend>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_DAYS.map((d) => {
                    const on = draft.weekdays.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        aria-pressed={on}
                        aria-label={weekdayName(d)}
                        onClick={() => set({ weekdays: on ? draft.weekdays.filter((x) => x !== d) : ([...draft.weekdays, d].sort((a, b) => a - b) as Weekday[]) })}
                        className={`grid min-h-11 min-w-11 place-items-center rounded-lg border px-2 text-sm ${on ? 'border-accent bg-accent-soft font-semibold text-accent' : 'border-border-strong text-muted hover:text-text'}`}
                      >
                        {weekdayName(d, true)}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <Button variant="ghost" className="min-h-10 px-3" onClick={() => set({ weekdays: WORK_DAYS })}>
                    {t('Vardagar', 'Weekdays')}
                  </Button>
                  <Button variant="ghost" className="min-h-10 px-3" onClick={() => set({ weekdays: ALL_DAYS })}>
                    {t('Alla dagar', 'Every day')}
                  </Button>
                </div>
                {shown?.code === 'weekdays' && (
                  <p className="mt-1 text-xs font-medium text-risk" role="alert">
                    {templateIssueText(shown)}
                  </p>
                )}
              </fieldset>

              <Field label={t('Kalender för passen', 'Calendar for the sessions')} htmlFor={fieldId('calendar')} hint={t('Bara dina egna kalendrar. Importerade kalendrar är skrivskyddade.', 'Only your own calendars. Imported calendars are read-only.')} error={shown?.code === 'calendar' ? templateIssueText(shown) : null}>
                <select id={fieldId('calendar')} className={inputCls} value={calendars.some((c) => c.id === draft.calendarId) ? draft.calendarId : ''} onChange={(e) => set({ calendarId: e.target.value })}>
                  {!calendars.some((c) => c.id === draft.calendarId) && <option value="">{t('Välj kalender', 'Choose calendar')}</option>}
                  {calendars.map((c) => (
                    <option key={c.id} value={c.id}>
                      {calendarName(c)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </details>

          {shown && (
            <p id={fieldId('issue')} className="rounded-lg border border-risk/60 bg-risk-bg px-3 py-2 text-sm" role="alert">
              {templateIssueText(shown)}
            </p>
          )}

          {confirmDelete && (
            <div className="rounded-lg border border-risk/60 bg-risk-bg p-3" role="group" aria-labelledby={fieldId('del')}>
              <p id={fieldId('del')} className="font-medium [overflow-wrap:anywhere]">
                {t(`Ta bort mallen "${existing?.name ?? ''}"?`, `Delete the template "${existing?.name ?? ''}"?`)}
              </p>
              <p className="mt-1">{t('Planer som redan lagts in påverkas inte. Regler som använder mallen pausas.', 'Plans already added are not affected. Rules that use the template are paused.')}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button onClick={() => setConfirmDelete(false)}>{t('Avbryt', 'Cancel')}</Button>
                <Button variant="danger" disabled={busy} onClick={() => void remove()}>
                  {t('Ta bort', 'Delete')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}
